if (!customElements.get('sticky-atc-bar')) {
  customElements.define('sticky-atc-bar', class StickyAtcBar extends HTMLElement {
    constructor() {
      super();
      this.observer = null;
      this.isVisible = false;
      this.drawerOpen = false;

      // Bind methods
      this._onBarClick = this._onBarClick.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
      this._onEscape = this._onEscape.bind(this);
      this._onBackdropClick = this._onBackdropClick.bind(this);
      this._onDocumentClick = this._onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.hasVariants = this.dataset.hasVariants === 'true';

      // Cache DOM references (deferred from constructor for custom element spec compliance)
      this.barBtn = this.querySelector('[data-sticky-atc-btn]');
      this.barBtnText = this.querySelector('[data-sticky-btn-text]');
      this.barPrice = this.querySelector('[data-sticky-price]');
      this.barComparePrice = this.querySelector('[data-sticky-compare-price]');
      this.barMicrocopy = this.querySelector('[data-sticky-microcopy]');
      this.barThumb = this.querySelector('.sticky-atc-bar__thumb');

      if (this.hasVariants) {
        this.backdrop = this.querySelector('[data-sticky-drawer-backdrop]');
        this.drawer = this.querySelector('[data-sticky-drawer]');
        this.drawerClose = this.querySelector('[data-sticky-drawer-close]');
        this.drawerPrice = this.querySelector('[data-sticky-drawer-price]');
        this.drawerComparePrice = this.querySelector('[data-sticky-drawer-compare-price]');
        this.drawerImage = this.querySelector('[data-sticky-drawer-image]');
        this.drawerAddBtn = this.querySelector('[data-sticky-drawer-add]');
        this.drawerAddText = this.querySelector('[data-sticky-drawer-add-text]');
        this.drawerStock = this.querySelector('[data-sticky-drawer-stock]');
      }

      this._setupIntersectionObserver();
      this._setupBarButton();
      this._setupVariantSync();

      if (this.hasVariants) {
        this._setupDrawer();
      }

      // Sync initial state from page
      this._syncFromPage();
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      document.body.classList.remove('sticky-atc-visible');
      document.body.classList.remove('sticky-atc-drawer-open');
      document.removeEventListener('keydown', this._onEscape);
      document.removeEventListener('click', this._onDocumentClick);
    }

    // =========================================================================
    // Intersection Observer — show/hide bar
    // =========================================================================

    _setupIntersectionObserver() {
      // The form ID IS the <form> element — find the submit button inside it
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (!form) return;

      const originalBtn = form.querySelector('[name="add"]');
      if (!originalBtn) return;

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          // Only show bar when the button has scrolled ABOVE the viewport
          // (boundingClientRect.bottom < 0 means the entire button is above the visible area)
          if (!entry.isIntersecting && entry.boundingClientRect.bottom < 0) {
            this._showBar();
          } else {
            this._hideBar();
          }
        });
      }, { threshold: 0 });

      this.observer.observe(originalBtn);
    }

    _showBar() {
      if (this.isVisible) return;
      this.isVisible = true;
      this.classList.add('sticky-atc-bar--visible');
      document.body.classList.add('sticky-atc-visible');
    }

    _hideBar() {
      if (!this.isVisible) return;
      this.isVisible = false;
      this.classList.remove('sticky-atc-bar--visible');
      document.body.classList.remove('sticky-atc-visible');
    }

    // =========================================================================
    // Bar button
    // =========================================================================

    _setupBarButton() {
      if (this.barBtn) {
        this.barBtn.addEventListener('click', this._onBarClick);
      }
    }

    _onBarClick() {
      if (this.barBtn.disabled || this.barBtn.getAttribute('aria-disabled') === 'true') return;

      if (this.hasVariants) {
        this._openDrawer();
      } else {
        this._submitOriginalForm();
      }
    }

    // =========================================================================
    // Submit the original product form (reuses all existing logic)
    // =========================================================================

    _submitOriginalForm() {
      // product-form-{sectionId} IS the <form> element itself (not a wrapper)
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (!form) return;

      // Find the original submit button — we click it to trigger ProductForm.onSubmitHandler
      const submitBtn = form.querySelector('[type="submit"]');
      if (!submitBtn || submitBtn.disabled || submitBtn.getAttribute('aria-disabled') === 'true') return;

      // Show loading state on our sticky bar buttons
      this._setLoading(true);

      // Listen for cart notification to know when add succeeds
      this._watchForCartSuccess();

      // Click the original form's submit button to trigger ProductForm.onSubmitHandler
      submitBtn.click();
    }

    _setLoading(loading) {
      if (loading) {
        this.barBtn.setAttribute('aria-disabled', 'true');
        this.barBtn.classList.add('loading');
        if (this.drawerAddBtn) {
          this.drawerAddBtn.disabled = true;
        }
      } else {
        this.barBtn.removeAttribute('aria-disabled');
        this.barBtn.classList.remove('loading');
        if (this.drawerAddBtn) {
          this.drawerAddBtn.disabled = false;
        }
      }
    }

    _watchForCartSuccess() {
      const notification = document.getElementById('cart-notification');
      let done = false;
      let observer = null;

      // Runs once, whichever signal arrives first
      const finish = (success) => {
        if (done) return;
        done = true;
        if (observer) observer.disconnect();
        document.removeEventListener('hp:side-cart:rendered', onSideCartRendered);
        if (success) {
          this._onAddSuccess();
        } else {
          this._setLoading(false);
        }
      };

      // The side cart renders instead of opening the notification popup
      const onSideCartRendered = () => finish(true);
      document.addEventListener('hp:side-cart:rendered', onSideCartRendered);

      if (!notification) {
        // No cart notification element — reset after delay
        setTimeout(() => finish(false), 2000);
        return;
      }

      // Watch for the notification becoming active
      observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (notification.classList.contains('active')) {
              finish(true);
              return;
            }
          }
        }
      });

      observer.observe(notification, { attributes: true, attributeFilter: ['class'] });

      // Also watch for errors on the original product-form custom element
      const form = document.getElementById(`product-form-${this.sectionId}`);
      const productFormEl = form ? form.closest('product-form') : null;
      if (productFormEl) {
        const errorWrapper = productFormEl.querySelector('.product-form__error-message-wrapper');
        if (errorWrapper) {
          const errorObserver = new MutationObserver(() => {
            if (!errorWrapper.hidden) {
              errorObserver.disconnect();
              finish(false);
            }
          });
          errorObserver.observe(errorWrapper, { attributes: true, attributeFilter: ['hidden'] });
          setTimeout(() => errorObserver.disconnect(), 10000);
        }
      }

      // Timeout fallback
      setTimeout(() => finish(false), 10000);
    }

    _onAddSuccess() {
      this._setLoading(false);

      // Close drawer if open
      if (this.drawerOpen) {
        this._closeDrawer();
      }

      // Show "Added" feedback on bar button
      const originalText = this.barBtnText.textContent;
      this.barBtnText.textContent = 'Added \u2713';
      this.barBtn.classList.add('sticky-atc-bar__btn--added');

      setTimeout(() => {
        this.barBtnText.textContent = originalText;
        this.barBtn.classList.remove('sticky-atc-bar__btn--added');
      }, 2000);
    }

    // =========================================================================
    // Variant sync — listen for changes on the page's variant selectors
    // =========================================================================

    _setupVariantSync() {
      const variantSelector = document.querySelector(
        `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
      );
      if (variantSelector) {
        variantSelector.addEventListener('change', this._onVariantChange);
      }
    }

    _onVariantChange() {
      // Use double rAF to let the page's variant logic run first
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._syncFromPage();
        });
      });
    }

    _syncFromPage() {
      const variantData = this._getVariantMetadata();
      const currentVariantId = this._getCurrentVariantId();

      if (!currentVariantId || !variantData) {
        this._updateBarForUnavailable();
        return;
      }

      const variant = variantData.variants.find(v => v.id == currentVariantId);
      if (!variant) {
        this._updateBarForUnavailable();
        return;
      }

      // Update prices
      const formattedPrice = this._formatMoney(variant.price);
      const isOnSale = variant.compare_at_price != null && variant.compare_at_price > variant.price;
      const formattedCompare = isOnSale ? this._formatMoney(variant.compare_at_price) : '';

      if (this.barPrice) this.barPrice.textContent = formattedPrice;
      if (this.barComparePrice) {
        this.barComparePrice.textContent = formattedCompare;
        this.barComparePrice.style.display = isOnSale ? '' : 'none';
      }

      if (this.drawerPrice) this.drawerPrice.textContent = formattedPrice;
      if (this.drawerComparePrice) {
        this.drawerComparePrice.textContent = formattedCompare;
        this.drawerComparePrice.style.display = isOnSale ? '' : 'none';
      }

      // Update image if variant has featured media (src must be a string URL)
      if (variant.featured_media && typeof variant.featured_media.src === 'string') {
        const imgSrc = variant.featured_media.src;
        if (this.barThumb) {
          this.barThumb.src = this._resizeImageUrl(imgSrc, 100);
        }
        if (this.drawerImage) {
          this.drawerImage.src = this._resizeImageUrl(imgSrc, 200);
        }
      }

      // Update availability and button labels
      if (variant.available) {
        this.barBtn.disabled = false;
        this.barBtn.removeAttribute('aria-disabled');
        if (this.drawerAddBtn) this.drawerAddBtn.disabled = false;

        this.barBtnText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        if (this.drawerAddText) {
          this.drawerAddText.textContent = window.variantStrings?.addToCart || 'Add to cart';
        }
      } else {
        this.barBtn.disabled = true;
        this.barBtnText.textContent = window.variantStrings?.soldOut || 'Sold out';
        if (this.drawerAddBtn) this.drawerAddBtn.disabled = true;
        if (this.drawerAddText) {
          this.drawerAddText.textContent = window.variantStrings?.soldOut || 'Sold out';
        }
      }

      // Update microcopy
      let microcopy = '';
      // Store facts free-delivery threshold, rendered onto the microcopy (snippets/sticky-atc-bar.liquid)
      const freeThreshold = Number(this.barMicrocopy && this.barMicrocopy.dataset.freeThreshold) || 4998;
      if (variant.available && variant.price >= freeThreshold) {
        microcopy = 'FREE EXPRESS DELIVERY';
      }
      if (variant.available && variant.stock_level === 'low') {
        microcopy = 'LAST FEW IN STOCK';
        this.barMicrocopy.classList.add('sticky-atc-bar__microcopy--low-stock');
      } else {
        this.barMicrocopy.classList.remove('sticky-atc-bar__microcopy--low-stock');
      }
      if (this.barMicrocopy) this.barMicrocopy.textContent = microcopy;

      // Update drawer stock indicator
      if (this.drawerStock) {
        if (variant.stock_level === 'low' && variant.available) {
          this.drawerStock.textContent = 'LAST FEW IN STOCK';
          this.drawerStock.className = 'sticky-atc-drawer__stock sticky-atc-drawer__stock--low';
        } else if (variant.available) {
          this.drawerStock.textContent = 'IN STOCK | 1-2 DAY EXPRESS DELIVERY';
          this.drawerStock.className = 'sticky-atc-drawer__stock sticky-atc-drawer__stock--in-stock';
        } else {
          this.drawerStock.textContent = 'OUT OF STOCK';
          this.drawerStock.className = 'sticky-atc-drawer__stock sticky-atc-drawer__stock--low';
        }
      }

      // Sync drawer radio buttons to match page selections
      if (this.hasVariants && this.drawer) {
        this._syncDrawerSelections();
      }
    }

    _updateBarForUnavailable() {
      this.barBtn.disabled = true;
      this.barBtnText.textContent = window.variantStrings?.unavailable || 'Unavailable';
      if (this.drawerAddBtn) this.drawerAddBtn.disabled = true;
      if (this.drawerAddText) {
        this.drawerAddText.textContent = window.variantStrings?.unavailable || 'Unavailable';
      }
      if (this.barMicrocopy) this.barMicrocopy.textContent = '';
    }

    // =========================================================================
    // Variant drawer
    // =========================================================================

    _setupDrawer() {
      if (!this.drawer) return;

      // Close handlers
      this.drawerClose.addEventListener('click', () => this._closeDrawer());
      this.backdrop.addEventListener('click', this._onBackdropClick);

      // Drawer option change -> sync to page
      this.drawer.querySelectorAll('input[type="radio"]').forEach(input => {
        input.addEventListener('change', (e) => this._onDrawerOptionChange(e));
      });

      // Drawer ATC button
      if (this.drawerAddBtn) {
        this.drawerAddBtn.addEventListener('click', () => this._submitOriginalForm());
      }
    }

    _openDrawer() {
      if (this.drawerOpen) return;
      this.drawerOpen = true;

      // Sync selections from page before showing
      this._syncDrawerSelections();

      this.backdrop.classList.add('sticky-atc-drawer__backdrop--visible');
      this.drawer.classList.add('sticky-atc-drawer--visible');
      document.body.classList.add('sticky-atc-drawer-open');
      document.addEventListener('keydown', this._onEscape);

      // Close on click outside drawer (deferred so the opening click doesn't immediately close)
      setTimeout(() => {
        document.addEventListener('click', this._onDocumentClick);
      }, 0);

      // Focus management
      this.drawer.addEventListener('transitionend', () => {
        if (typeof trapFocus === 'function') {
          trapFocus(this.drawer);
        } else {
          this.drawer.focus();
        }
      }, { once: true });
    }

    _closeDrawer() {
      if (!this.drawerOpen) return;
      this.drawerOpen = false;

      this.backdrop.classList.remove('sticky-atc-drawer__backdrop--visible');
      this.drawer.classList.remove('sticky-atc-drawer--visible');
      document.body.classList.remove('sticky-atc-drawer-open');
      document.removeEventListener('keydown', this._onEscape);
      document.removeEventListener('click', this._onDocumentClick);

  if (typeof removeTrapFocus === 'function') {
        removeTrapFocus();
      }
      // Blur any focused element to prevent iOS showing a focus ring on the bar button
      if (document.activeElement) document.activeElement.blur();
    }

    _onEscape(e) {
      if (e.code === 'Escape') {
        this._closeDrawer();
      }
    }

    _onBackdropClick(e) {
      if (e.target === this.backdrop) {
        this._closeDrawer();
      }
    }

    _onDocumentClick(e) {
      if (this.drawerOpen && !this.drawer.contains(e.target)) {
        this._closeDrawer();
      }
    }

    _syncDrawerSelections() {
      // Read current selections from the PAGE's variant selectors and sync to drawer
      const variantSelector = document.querySelector(
        `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
      );
      if (!variantSelector) return;

      // Match by position (data-option-index) to avoid handle-format mismatches
      const pageFieldsets = variantSelector.querySelectorAll('fieldset');
      pageFieldsets.forEach((fieldset, index) => {
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (!checkedRadio) return;
        const optionValue = checkedRadio.value;

        const drawerGroup = this.drawer.querySelector(`[data-option-index="${index}"]`);
        if (drawerGroup) {
          const drawerRadio = drawerGroup.querySelector(`input[value="${CSS.escape(optionValue)}"]`);
          if (drawerRadio && !drawerRadio.checked) {
            drawerRadio.checked = true;
          }
        }
      });

      // Handle selects (variant-selects fallback)
      const pageSelects = variantSelector.querySelectorAll('select');
      pageSelects.forEach((select, index) => {
        const optionValue = select.value;
        const drawerGroup = this.drawer.querySelector(`[data-option-index="${index}"]`);
        if (drawerGroup) {
          const drawerRadio = drawerGroup.querySelector(`input[value="${CSS.escape(optionValue)}"]`);
          if (drawerRadio) {
            drawerRadio.checked = true;
          }
        }
      });
    }

    _onDrawerOptionChange(e) {
      const input = e.target;
      const optionName = input.dataset.optionName;
      const optionValue = input.value;
      const optionPosition = input.dataset.optionPosition;

      // Find the corresponding PAGE selector and update it
      const variantSelector = document.querySelector(
        `variant-radios[data-section="${this.sectionId}"], variant-selects[data-section="${this.sectionId}"]`
      );
      if (!variantSelector) return;

      // Try radio buttons first — match by name and value with CSS.escape for safety
      const pageRadio = variantSelector.querySelector(
        `input[type="radio"][name="${CSS.escape(optionName)}"][value="${CSS.escape(optionValue)}"]`
      );
      if (pageRadio) {
        pageRadio.checked = true;
        variantSelector.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }

      // Try selects (variant-selects) — match by position
      if (optionPosition) {
        const selectIndex = parseInt(optionPosition, 10) - 1;
        const selects = variantSelector.querySelectorAll('select');
        if (selects[selectIndex]) {
          selects[selectIndex].value = optionValue;
          variantSelector.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // =========================================================================
    // Helpers
    // =========================================================================

    _getCurrentVariantId() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (!form) return null;
      const input = form.querySelector('input[name="id"]');
      return input ? input.value : null;
    }

    _getVariantMetadata() {
      const script = document.getElementById(`variant-metadata-${this.sectionId}`);
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        return null;
      }
    }

    _formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
        return Shopify.formatMoney(cents, (typeof theme !== 'undefined' && theme.moneyFormat) || '\u00a3{{amount}}');
      }
      const pounds = (cents / 100).toFixed(2);
      return `\u00a3${pounds}`;
    }

    _resizeImageUrl(src, width) {
      if (!src || typeof src !== 'string') return src;
      if (src.includes('cdn.shopify.com')) {
        // Use modern Shopify CDN query parameter approach
        try {
          const base = src.startsWith('//') ? 'https:' + src : src;
          const url = new URL(base);
          url.searchParams.set('width', width);
          return src.startsWith('//') ? url.href.replace(url.origin, '') : url.href;
        } catch (e) {
          return src;
        }
      }
      return src;
    }
  });
}
